import { View } from 'common/types/view';
import React, { useState, useCallback, useMemo, ComponentType, ReactNode } from 'react';

/**
 * Hook to manage modal state for action dropdown items
 *
 * Supports both legacy modal components and ForgeDialog-based components.
 *
 * @example Legacy modal usage
 * ```tsx
 * const { currentModal, openModal, closeModal } = useActionModal();
 * openModal(AccessManager, { mode: 'change_audience' }, view);
 * return <>{currentModal}</>;
 * ```
 *
 * @example ForgeDialog usage
 * ```tsx
 * const { isModalOpen, openModal, closeModal } = useActionModal();
 * openModal(); // Just sets isModalOpen to true
 * return <ForgeDialog open={isModalOpen} on-forge-dialog-close={closeModal}>...</ForgeDialog>;
 * ```
 */
export function useActionModal() {
  const [currentModal, setCurrentModal] = useState<ReactNode | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Opens a modal. Can be used in two ways:
   * 1. Pass a Modal component for legacy modals (will render the component)
   * 2. Call with no arguments to just set isModalOpen=true (for ForgeDialog pattern)
   */
  const openModal = useCallback((Modal?: ComponentType<any>, modalProps: any = {}, view?: View) => {
    if (Modal) {
      // Legacy pattern: render the modal component
      setCurrentModal(
        <Modal
          view={view}
          onDismiss={() => {
            setCurrentModal(null);
            setIsModalOpen(false);
          }}
          {...modalProps}
        />,
      );
    }
    setIsModalOpen(true);
  }, []);

  /**
   * Closes the modal and clears state
   */
  const closeModal = useCallback(() => {
    setCurrentModal(null);
    setIsModalOpen(false);
  }, []);

  return useMemo(
    () => ({
      currentModal,
      isModalOpen,
      openModal,
      closeModal,
    }),
    [currentModal, isModalOpen, openModal, closeModal],
  );
}
